import { RideType, OrderFormState, MapMarker, isScheduleRide } from "./types";
import { isScheduleDateTimeInPast, joinScheduleDateTime } from "./scheduleDateTime";
import { DEFAULT_PHONE_DIAL_CODE } from "./phoneDialCodes";
import {
  DEFAULT_PICKUP_ADDRESS,
  DEFAULT_PICKUP_COORDS,
  MIN_LUGGAGE,
  MIN_PASSENGERS,
} from "./orderFormConstants";
import { GeocodeSuggestion } from "./types";
import { getElitDurationVehicle } from "./elitVehicleSelection";
import { getStoredPassengerUserId } from "./rideOrderPassengerStorage";
import { getVehicleById, ORDER_VEHICLES } from "./vehicleData";
import { createEmptyWaypoint, createWaypointId, toWaypointMarkerId } from "./routeMarkers";
import serviceEcoImg from "../../static/ride-order/service/service-eco.png";
import serviceBizImg from "../../static/ride-order/service/service-biz.png";
import serviceLuxImg from "../../static/ride-order/service/service-lux.png";
import { NowServiceTier } from "./types";

export type NowServiceTierOption = {
  id: NowServiceTier;
  label: string;
  carClass: number;
  image: string;
};

export const NOW_SERVICE_TIER_OPTIONS: NowServiceTierOption[] = [
  { id: "eco", label: "ECO", carClass: 2, image: serviceEcoImg },
  { id: "biz", label: "BIZ", carClass: 4, image: serviceBizImg },
];

export function nowTierFromCarClass(carClass: number): NowServiceTier {
  const match = NOW_SERVICE_TIER_OPTIONS.find((tier) => tier.carClass === carClass);
  return match?.id ?? "eco";
}

export function carClassFromNowTier(tier: NowServiceTier): number {
  const match = NOW_SERVICE_TIER_OPTIONS.find((t) => t.id === tier);
  return match?.carClass ?? 2;
}

export function getOrderSubmitLabel(rideType: RideType): string {
  return isScheduleRide(rideType) ? "Schedule CAR:GO Elit" : "Order Now";
}

export function createInitialFormState(rideType: RideType): OrderFormState {
  const vehicle = getVehicleById(ORDER_VEHICLES[0].id);

  const base: OrderFormState = {
    vehicleId: vehicle.id,
    carClass: 2,
    pickup: "",
    dropoff: "",
    pickupCoords: null,
    dropoffCoords: null,
    waypoints: [],
    name: "",
    phoneDialCode: DEFAULT_PHONE_DIAL_CODE,
    phone: "",
    userId: getStoredPassengerUserId(),
    date: "",
    time: "",
    passengers: 1,
    luggage: 0,
    transferMode: true,
    durationHours: 0,
    comment: "",
    flightNumber: "",
    childSeat: false,
  };

  if (isScheduleRide(rideType)) {
    return applyDefaultPickupState({
      ...base,
      carClass: null,
      transferMode: true,
      durationHours: 0,
    });
  }

  return applyDefaultPickupState(base);
}

export function applyDefaultPickupState(prev: OrderFormState): OrderFormState {
  return {
    ...prev,
    pickup: DEFAULT_PICKUP_ADDRESS,
    pickupCoords: DEFAULT_PICKUP_COORDS,
  };
}

export function createWaypoint() {
  return createEmptyWaypoint(createWaypointId());
}

export function applyEndpointSelect(
  prev: OrderFormState,
  endpoint: "pickup" | "dropoff",
  suggestion: GeocodeSuggestion
): OrderFormState {
  const coords = { lat: suggestion.lat, lng: suggestion.lng };
  const address = suggestion.formattedAddress;
  const normalized = address.trim().toLowerCase();

  if (endpoint === "pickup") {
    const syncDropoff = prev.dropoff.trim().toLowerCase() === normalized;
    return {
      ...prev,
      pickup: address,
      pickupCoords: coords,
      ...(syncDropoff ? { dropoffCoords: coords } : {}),
    };
  }

  const syncPickup = prev.pickup.trim().toLowerCase() === normalized;
  return {
    ...prev,
    dropoff: address,
    dropoffCoords: coords,
    ...(syncPickup ? { pickupCoords: coords } : {}),
  };
}

export function buildMapMarkers(
  pickup: string,
  dropoff: string,
  pickupCoords: { lat: number; lng: number } | null,
  dropoffCoords: { lat: number; lng: number } | null,
  waypoints: { id: string; address: string; coords: { lat: number; lng: number } | null }[]
): MapMarker[] {
  const markers: MapMarker[] = [];

  if (pickupCoords) {
    markers.push({
      id: "pickup",
      lat: pickupCoords.lat,
      lng: pickupCoords.lng,
      label: pickup.trim() || "Pickup",
    });
  }

  waypoints.forEach((waypoint, index) => {
    if (!waypoint.coords) return;
    markers.push({
      id: toWaypointMarkerId(waypoint.id),
      lat: waypoint.coords.lat,
      lng: waypoint.coords.lng,
      label: waypoint.address.trim() || `Stop ${index + 1}`,
      waypointIndex: index + 1,
    });
  });

  if (dropoffCoords) {
    markers.push({
      id: "dropoff",
      lat: dropoffCoords.lat,
      lng: dropoffCoords.lng,
      label: dropoff.trim() || "Drop-off",
    });
  }

  return markers;
}

export function canSubmitForm(form: OrderFormState, rideType: RideType): boolean {
  if (!form.pickupCoords || !form.dropoffCoords) return false;
  if (!form.pickup.trim() || !form.dropoff.trim()) return false;
  if (form.waypoints.some((wp) => wp.address.trim() && !wp.coords)) return false;
  if (!form.userId) return false;

  if (isScheduleRide(rideType)) {
    if (!form.date || !form.time) return false;
    const scheduledAt = joinScheduleDateTime(form.date, form.time);
    if (!scheduledAt || isScheduleDateTimeInPast(scheduledAt)) return false;
    if (!form.vehicleId) return false;
    if (form.passengers < MIN_PASSENGERS || form.luggage < MIN_LUGGAGE) return false;

    const vehicle = getElitDurationVehicle(form.vehicleId);
    if (!form.transferMode && form.durationHours < vehicle.minHours) return false;
  }

  return true;
}

export function applyRegularTransferMode(prev: OrderFormState): OrderFormState {
  return {
    ...prev,
    transferMode: true,
    durationHours: 0,
  };
}

export function applyHourlyRentalMode(prev: OrderFormState): OrderFormState {
  const vehicle = getElitDurationVehicle(prev.vehicleId);
  return {
    ...prev,
    transferMode: false,
    durationHours: prev.durationHours > 0 ? prev.durationHours : vehicle.minHours,
  };
}
